import { useRef, useState, useCallback } from 'react';

const AUDIO_CONSTRAINTS = {
  sampleRate: 16000,
  channelCount: 1,
  echoCancellation: true,
  noiseSuppression: true,
};

const PREFERRED_MIME_TYPE = 'audio/webm;codecs=opus';
const AUDIO_BITRATE = 128000;
const CHUNK_INTERVAL_MS = 1000;

interface UseBatchRecorderOptions {
  onAudioReady: (blob: Blob) => void;
}

/**
 * Encapsulates MediaRecorder logic for batch (record-then-send) dictation.
 * Records audio locally, then delivers the complete blob via onAudioReady.
 */
export function useBatchRecorder({ onAudioReady }: UseBatchRecorderOptions) {
  const [isRecording, setIsRecording] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const onAudioReadyRef = useRef(onAudioReady);
  onAudioReadyRef.current = onAudioReady;

  const cleanup = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const start = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: AUDIO_CONSTRAINTS,
    });
    streamRef.current = stream;

    const recorder = MediaRecorder.isTypeSupported(PREFERRED_MIME_TYPE)
      ? new MediaRecorder(stream, {
          mimeType: PREFERRED_MIME_TYPE,
          audioBitsPerSecond: AUDIO_BITRATE,
        })
      : new MediaRecorder(stream);

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, {
        type: recorder.mimeType || PREFERRED_MIME_TYPE,
      });
      chunksRef.current = [];
      if (blob.size > 0) {
        onAudioReadyRef.current(blob);
      }
    };

    mediaRecorderRef.current = recorder;
    recorder.start(CHUNK_INTERVAL_MS);
    setIsRecording(true);
  }, []);

  const stop = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsRecording(false);
  }, []);

  /** Stop recording without delivering audio (e.g. on modal close). */
  const cancel = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.onstop = null;
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    }
    cleanup();
    setIsRecording(false);
  }, [cleanup]);

  return { isRecording, start, stop, cancel };
}
